/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  Undo2, Loader2, ArrowRight, Building2, FlaskConical, Search,
  RotateCcw, AlertTriangle, CheckCircle2, Users, MoveRight, Sparkles,
} from 'lucide-react';

const API = '/api';

/* ------------------------------------------------------------------ */
/* Tab 1 — Un-merge (reverse a company merge)                          */
/* ------------------------------------------------------------------ */
function UnmergeTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [moveContacts, setMoveContacts] = useState(true);
  const [applying, setApplying] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${API}/admin/merge-biotech-companies/audit?limit=200`, { withCredentials: true });
      setRows(data.rows || []);
    } catch (e) { toast.error('Could not load merge history'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openInspect = async (row) => {
    setOpen(true); setPreview(null); setPreviewing(true);
    setSelected(new Set()); setMoveContacts(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/merge-biotech-companies/restore`,
        { audit_id: row.audit_id, dry_run: true }, { withCredentials: true });
      setPreview(data);
      setSelected(new Set((data.survivor_assets || []).filter(a => a.suggested).map(a => a.id)));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not build preview'); setOpen(false);
    } finally { setPreviewing(false); }
  };

  const toggle = (id) => setSelected(prev => {
    const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n;
  });

  const applyReverse = async () => {
    if (!preview) return;
    setApplying(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/merge-biotech-companies/restore`,
        { audit_id: preview.audit_id, asset_ids: Array.from(selected), move_contacts: moveContacts, dry_run: false },
        { withCredentials: true });
      toast.success(`Restored “${data.drop_name}” — ${data.assets_moved_back} asset(s) moved back`);
      setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Reverse failed'); }
    finally { setApplying(false); }
  };

  const filtered = rows.filter(r => {
    if (!q.trim()) return true;
    const s = q.toLowerCase();
    return (r.survivor_name || '').toLowerCase().includes(s) || (r.drop_name || '').toLowerCase().includes(s);
  });

  return (
    <>
      <p className="text-sm text-slate-500 mb-4">
        Use this only when a merge combined two <span className="font-semibold">different</span> companies into one.
        It recreates the lost company and moves its assets back.
      </p>
      <div className="relative mb-5 max-w-sm">
        <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <Input data-testid="unmerge-search" value={q} onChange={e => setQ(e.target.value)}
          placeholder="Search merged company names…" className="pl-9" />
      </div>
      {loading ? (
        <div className="flex items-center gap-2 text-slate-500 py-16 justify-center">
          <Loader2 className="w-5 h-5 animate-spin" /> Loading merge history…
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-14 text-slate-500 text-sm">
          No matching merges found. If a company still exists but shows the wrong assets, use the
          <span className="font-semibold"> “Fix Asset Ownership”</span> tab instead.
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(r => (
            <Card key={r.audit_id} className="border-slate-200" data-testid="merge-row">
              <CardContent className="p-4 flex items-center justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge variant="outline" className="bg-rose-50 text-rose-700 border-rose-200">
                      <Building2 className="w-3 h-3 mr-1" /> Lost: {r.drop_name || '—'}
                    </Badge>
                    <ArrowRight className="w-3.5 h-3.5 text-slate-400" />
                    <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
                      Kept: {r.survivor_name || '—'}
                    </Badge>
                  </div>
                  <div className="text-xs text-slate-400 mt-1.5">
                    Merged {r.created_at ? new Date(r.created_at).toLocaleString() : ''}
                    {r.actor_email ? ` · by ${r.actor_email}` : ''}
                  </div>
                </div>
                <Button size="sm" variant="outline"
                  className="shrink-0 border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100"
                  onClick={() => openInspect(r)} data-testid="inspect-merge-btn">
                  <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Inspect &amp; Reverse
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl" data-testid="reverse-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Undo2 className="w-4 h-4 text-amber-700" /> Reverse merge</DialogTitle>
            <DialogDescription>
              {preview ? (<>Restore <span className="font-semibold text-slate-800">{preview.drop_name}</span> and
                move the ticked assets back off <span className="font-semibold text-slate-800">{preview.survivor_name}</span>.</>) : 'Building preview…'}
            </DialogDescription>
          </DialogHeader>
          {previewing ? (
            <div className="flex items-center gap-2 text-slate-500 py-10 justify-center"><Loader2 className="w-5 h-5 animate-spin" /> Preparing preview…</div>
          ) : preview ? (
            <div className="space-y-4">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-semibold text-slate-700">Tick the assets that belong to “{preview.drop_name}”</p>
                  <span className="text-xs text-slate-400">{selected.size} selected</span>
                </div>
                <div className="border border-slate-200 rounded-lg max-h-72 overflow-y-auto divide-y divide-slate-100">
                  {(preview.survivor_assets || []).length === 0 ? (
                    <div className="p-4 text-sm text-slate-500">No assets found on the kept company.</div>
                  ) : (preview.survivor_assets || []).map(a => (
                    <label key={a.id} className="flex items-start gap-3 p-3 hover:bg-slate-50 cursor-pointer" data-testid="asset-toggle">
                      <Checkbox checked={selected.has(a.id)} onCheckedChange={() => toggle(a.id)} className="mt-0.5" />
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <FlaskConical className="w-3.5 h-3.5 text-violet-500 shrink-0" />
                          <span className="text-sm font-medium text-slate-800">{a.asset_name}</span>
                          {a.suggested && <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 text-[10px] py-0">suggested</Badge>}
                        </div>
                        <div className="text-xs text-slate-400 mt-0.5">{[a.stage, a.therapeutic_area, a.indication].filter(Boolean).join(' · ') || '—'}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
                <Checkbox checked={moveContacts} onCheckedChange={v => setMoveContacts(!!v)} />
                Also detach the restored company’s contacts from “{preview.survivor_name}”
              </label>
              <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800">
                <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" /> This action is logged and reversible. Only the ticked assets move.
              </div>
            </div>
          ) : null}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={applying}>Cancel</Button>
            <Button onClick={applyReverse} disabled={applying || previewing || !preview || selected.size === 0}
              className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="confirm-reverse-btn">
              {applying ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1.5" />}
              Reverse merge ({selected.size})
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

/* ------------------------------------------------------------------ */
/* A small biotech typeahead used for source + target pickers          */
/* ------------------------------------------------------------------ */
function BiotechPicker({ label, value, onPick, testId }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const timer = useRef(null);

  const search = (term) => {
    setQ(term); setOpen(true);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      if (!term.trim()) { setResults([]); return; }
      setLoading(true);
      try {
        const { data } = await axios.get(
          `${API}/admin/biotech-lookup?q=${encodeURIComponent(term)}`, { withCredentials: true });
        setResults(data.companies || []);
      } catch (e) { /* ignore */ }
      finally { setLoading(false); }
    }, 250);
  };

  return (
    <div className="relative">
      <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{label}</label>
      <div className="relative mt-1">
        <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <Input data-testid={testId} value={value ? value.company_name : q}
          onChange={e => { onPick(null); search(e.target.value); }}
          onFocus={() => setOpen(true)}
          placeholder="Type a company name…" className="pl-9" />
      </div>
      {open && !value && (q.trim() || loading) && (
        <div className="absolute z-20 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg max-h-64 overflow-y-auto">
          {loading ? (
            <div className="p-3 text-sm text-slate-400 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Searching…</div>
          ) : results.length === 0 ? (
            <div className="p-3 text-sm text-slate-400">No companies match.</div>
          ) : results.map(c => (
            <button key={c.id} type="button" data-testid="picker-option"
              onClick={() => { onPick(c); setOpen(false); }}
              className="w-full text-left px-3 py-2 hover:bg-slate-50 flex items-center justify-between gap-2">
              <span className="text-sm text-slate-800 truncate">{c.company_name}</span>
              <Badge variant="outline" className="text-[10px] text-slate-500 shrink-0">{c.asset_count} assets</Badge>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Tab 2 — Fix Asset Ownership (reassign assets to correct company)    */
/* ------------------------------------------------------------------ */
function FixOwnershipTab() {
  const [source, setSource] = useState(null);
  const [target, setTarget] = useState(null);
  const [newTargetName, setNewTargetName] = useState('');
  const [assets, setAssets] = useState([]);
  const [loadingAssets, setLoadingAssets] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [applying, setApplying] = useState(false);
  const [auditing, setAuditing] = useState(false);
  const [audit, setAudit] = useState({});   // asset_id -> finding

  useEffect(() => {
    if (!source) { setAssets([]); setSelected(new Set()); setAudit({}); return; }
    (async () => {
      setLoadingAssets(true); setAudit({});
      try {
        const { data } = await axios.get(
          `${API}/admin/biotech-assets?company_id=${source.id}`, { withCredentials: true });
        setAssets(data.assets || []);
        setSelected(new Set());
      } catch (e) { toast.error('Could not load assets'); }
      finally { setLoadingAssets(false); }
    })();
  }, [source]);

  const runAudit = async () => {
    if (!source) return;
    setAuditing(true);
    try {
      const { data } = await axios.post(`${API}/admin/asset-ownership-audit`,
        { company_id: source.id }, { withCredentials: true });
      const map = {};
      (data.findings || []).forEach(f => { map[f.asset_id] = f; });
      setAudit(map);
      // Auto-tick the AI-flagged mismatches.
      setSelected(new Set((data.findings || []).filter(f => f.mismatch).map(f => f.asset_id)));
      toast.success(`AI checked ${data.assets_checked} asset(s) — ${data.mismatches} look mis-assigned`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'AI check failed'); }
    finally { setAuditing(false); }
  };

  const toggle = (id) => setSelected(prev => {
    const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n;
  });

  const targetName = target ? target.company_name : newTargetName.trim();
  const canMove = source && selected.size > 0 && targetName;

  const move = async () => {
    if (!canMove) return;
    setApplying(true);
    try {
      const payload = { asset_ids: Array.from(selected), dry_run: false };
      if (target) payload.to_company_id = target.id;
      else payload.to_company_name = newTargetName.trim();
      const { data } = await axios.post(`${API}/admin/reassign-assets`, payload, { withCredentials: true });
      toast.success(`Moved ${data.assets_moved} asset(s) to “${data.target_company_name}”` + (data.target_created ? ' (new company created)' : ''));
      const { data: refreshed } = await axios.get(`${API}/admin/biotech-assets?company_id=${source.id}`, { withCredentials: true });
      setAssets(refreshed.assets || []); setSelected(new Set());
      setAudit(prev => { const n = { ...prev }; Array.from(selected).forEach(id => delete n[id]); return n; });
    } catch (e) { toast.error(e?.response?.data?.detail || 'Move failed'); }
    finally { setApplying(false); }
  };

  return (
    <>
      <p className="text-sm text-slate-500 mb-5">
        When a company shows assets it doesn’t actually own (e.g. OneChain showing OSE’s Tedopi),
        pick the company that <span className="font-semibold">currently</span> holds the assets, tick the wrong ones,
        and move them to the <span className="font-semibold">correct</span> company.
      </p>

      <div className="grid md:grid-cols-2 gap-5 mb-6">
        <BiotechPicker label="1 · Company that currently holds the assets" value={source}
          onPick={setSource} testId="source-picker" />
        <div>
          <BiotechPicker label="2 · Move selected assets to (correct company)" value={target}
            onPick={(c) => { setTarget(c); if (c) setNewTargetName(''); }} testId="target-picker" />
          {!target && (
            <p className="text-xs text-slate-400 mt-1">
              …or type a new company name to create it:&nbsp;
              <Input value={newTargetName} onChange={e => setNewTargetName(e.target.value)}
                placeholder="New company name (optional)" className="mt-1 h-8" data-testid="new-target-name" />
            </p>
          )}
        </div>
      </div>

      {source && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-semibold text-slate-700">
              Assets currently under “{source.company_name}”
            </p>
            <div className="flex items-center gap-3">
              <span className="text-xs text-slate-400">{selected.size} selected</span>
              <Button size="sm" variant="outline" onClick={runAudit} disabled={auditing || assets.length === 0}
                className="border-indigo-300 bg-indigo-50 text-indigo-700 hover:bg-indigo-100" data-testid="ai-check-btn">
                {auditing ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1.5" />}
                AI check ownership
              </Button>
            </div>
          </div>
          {Object.keys(audit).length > 0 && (
            <div className="text-xs text-slate-500 mb-2 bg-slate-50 border border-slate-200 rounded p-2">
              AI verified each asset’s real developer below. Flagged rows are auto-ticked — <span className="font-semibold">please confirm</span> before moving (generic codes can be guessed wrong).
            </div>
          )}
          {loadingAssets ? (
            <div className="flex items-center gap-2 text-slate-500 py-10 justify-center"><Loader2 className="w-5 h-5 animate-spin" /> Loading assets…</div>
          ) : assets.length === 0 ? (
            <div className="text-center py-10 text-slate-500 text-sm">No assets under this company.</div>
          ) : (
            <div className="border border-slate-200 rounded-lg max-h-80 overflow-y-auto divide-y divide-slate-100">
              {assets.map(a => {
                const f = audit[a.id];
                return (
                <label key={a.id} className={`flex items-start gap-3 p-3 hover:bg-slate-50 cursor-pointer ${f?.mismatch ? 'bg-rose-50/50' : ''}`} data-testid="fix-asset-toggle">
                  <Checkbox checked={selected.has(a.id)} onCheckedChange={() => toggle(a.id)} className="mt-0.5" />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <FlaskConical className="w-3.5 h-3.5 text-violet-500 shrink-0" />
                      <span className="text-sm font-medium text-slate-800">{a.asset_name}</span>
                      {f?.mismatch && (
                        <Badge variant="outline" className="bg-rose-50 text-rose-700 border-rose-200 text-[10px] py-0">
                          likely {f.verified_developer} ({f.confidence}%)
                        </Badge>
                      )}
                      {f && !f.mismatch && f.confidence >= 75 && (
                        <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px] py-0">
                          ownership looks correct
                        </Badge>
                      )}
                    </div>
                    <div className="text-xs text-slate-400 mt-0.5">
                      {[a.stage, a.therapeutic_area, a.indication].filter(Boolean).join(' · ') || '—'}
                      {a.source ? <span className="ml-2 text-slate-300">· from: {a.source}</span> : null}
                    </div>
                  </div>
                </label>
                );
              })}
            </div>
          )}

          <div className="flex items-center justify-between mt-4">
            <div className="text-xs text-slate-400 flex items-center gap-2">
              <AlertTriangle className="w-3.5 h-3.5" /> Logged &amp; reversible. Only ticked assets move.
            </div>
            <Button onClick={move} disabled={!canMove || applying}
              className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="move-assets-btn">
              {applying ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <MoveRight className="w-4 h-4 mr-1.5" />}
              Move {selected.size} asset(s){targetName ? ` → ${targetName}` : ''}
            </Button>
          </div>
        </div>
      )}
    </>
  );
}

/* ------------------------------------------------------------------ */
/* Tab 3 — AI Scan (DB-wide, no names needed)                          */
/* ------------------------------------------------------------------ */
function AiScanTab() {
  const [status, setStatus] = useState(null);
  const [findings, setFindings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fixing, setFixing] = useState({});   // asset_id -> bool
  const pollRef = useRef(null);

  const refresh = useCallback(async () => {
    try {
      const [s, f] = await Promise.all([
        axios.get(`${API}/admin/asset-ownership-audit/scan-status`, { withCredentials: true }),
        axios.get(`${API}/admin/asset-ownership-audit/findings?flagged_only=true`, { withCredentials: true }),
      ]);
      setStatus(s.data);
      setFindings(f.data.findings || []);
    } catch (e) { /* ignore */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    refresh();
    pollRef.current = setInterval(refresh, 6000);
    return () => clearInterval(pollRef.current);
  }, [refresh]);

  const start = async (restart = false) => {
    try {
      await axios.post(`${API}/admin/asset-ownership-audit/scan-all`,
        { sources_mode: 'imported', restart }, { withCredentials: true });
      toast.success(restart
        ? 'Fresh scan started — re-verifying every imported/AI-created asset'
        : 'Scan resumed — continuing from where it left off');
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not start scan'); }
  };

  const stop = async () => {
    try {
      await axios.post(`${API}/admin/asset-ownership-audit/scan-stop`, {}, { withCredentials: true });
      toast.success('Scan stopping…'); refresh();
    } catch (e) { toast.error('Could not stop scan'); }
  };

  const fixOne = async (f) => {
    setFixing(prev => ({ ...prev, [f.asset_id]: true }));
    try {
      const { data } = await axios.post(`${API}/admin/reassign-assets`,
        { asset_ids: [f.asset_id], to_company_name: f.verified_developer, dry_run: false },
        { withCredentials: true });
      toast.success(`“${f.asset_name}” moved to ${data.target_company_name}` + (data.target_created ? ' (new company created)' : ''));
      setFindings(prev => prev.filter(x => x.asset_id !== f.asset_id));
    } catch (e) { toast.error(e?.response?.data?.detail || 'Fix failed'); }
    finally { setFixing(prev => ({ ...prev, [f.asset_id]: false })); }
  };

  const dismissOne = async (f) => {
    try {
      await axios.post(`${API}/admin/asset-ownership-audit/dismiss`,
        { asset_id: f.asset_id }, { withCredentials: true });
      setFindings(prev => prev.filter(x => x.asset_id !== f.asset_id));
    } catch (e) { toast.error('Could not dismiss'); }
  };

  const running = status?.status === 'running';
  const pct = status?.total ? Math.round((status.checked / status.total) * 100) : 0;
  // A scan that stopped with work remaining can be resumed from its checkpoint.
  const resumable = !running && status?.status !== 'done'
    && (status?.checked || 0) > 0 && (status?.checked || 0) < (status?.total || 0);

  return (
    <>
      <p className="text-sm text-slate-500 mb-4">
        Scans your <span className="font-semibold">whole database</span> — no company names needed. The AI verifies each
        imported/AI-created asset’s true developer and lists every one that looks mis-assigned, with a one-click fix.
      </p>

      <Card className="border-slate-200 mb-5">
        <CardContent className="p-4 flex items-center justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <Badge variant="outline" className={
                running ? 'bg-indigo-50 text-indigo-700 border-indigo-200'
                  : status?.status === 'done' ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                  : 'bg-slate-50 text-slate-600'}>
                {running ? 'Scanning…' : (status?.status || 'idle')}
              </Badge>
              {status?.total ? (
                <span className="text-sm text-slate-600">
                  {status.checked}/{status.total} assets checked · <span className="font-semibold text-rose-600">{status.mismatches} flagged</span>
                </span>
              ) : null}
            </div>
            {running && (
              <div className="w-64 h-1.5 bg-slate-100 rounded-full mt-2 overflow-hidden">
                <div className="h-full bg-indigo-500 transition-all" style={{ width: `${pct}%` }} />
              </div>
            )}
            {status?.error && <p className="text-xs text-rose-600 mt-1">{status.error}</p>}
          </div>
          {running ? (
            <Button size="sm" variant="outline" onClick={stop} className="border-rose-300 text-rose-700 hover:bg-rose-50" data-testid="scan-stop-btn">
              Stop scan
            </Button>
          ) : resumable ? (
            <div className="flex items-center gap-2">
              <Button size="sm" onClick={() => start(false)} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="scan-resume-btn">
                <Sparkles className="w-3.5 h-3.5 mr-1.5" /> Resume scan ({status.checked}/{status.total})
              </Button>
              <Button size="sm" variant="ghost" onClick={() => start(true)} className="text-slate-500 hover:text-slate-700" data-testid="scan-restart-btn">
                Restart from scratch
              </Button>
            </div>
          ) : (
            <Button size="sm" onClick={() => start(false)} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="scan-start-btn">
              <Sparkles className="w-3.5 h-3.5 mr-1.5" /> Scan entire database
            </Button>
          )}
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex items-center gap-2 text-slate-500 py-10 justify-center"><Loader2 className="w-5 h-5 animate-spin" /> Loading findings…</div>
      ) : findings.length === 0 ? (
        <div className="text-center py-12 text-slate-500 text-sm">
          No flagged assets{status?.status === 'done' ? ' — everything looks correctly assigned 🎉' : ' yet. Run a scan to populate this list.'}
        </div>
      ) : (
        <div className="space-y-2">
          {findings.map(f => (
            <Card key={f.asset_id} className="border-rose-100 bg-rose-50/40" data-testid="scan-finding-row">
              <CardContent className="p-3.5 flex items-center justify-between gap-4 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <FlaskConical className="w-3.5 h-3.5 text-violet-500 shrink-0" />
                    <span className="text-sm font-semibold text-slate-800">{f.asset_name}</span>
                    <span className="text-xs text-slate-500">now under</span>
                    <Badge variant="outline" className="bg-white text-slate-700">{f.company_name || '—'}</Badge>
                    <ArrowRight className="w-3 h-3 text-slate-400" />
                    <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
                      {f.verified_developer} ({f.confidence}%)
                    </Badge>
                  </div>
                  <div className="text-xs text-slate-400 mt-1">
                    {[f.stage, f.indication].filter(Boolean).join(' · ')}
                    {f.source ? ` · came from: ${f.source}` : ''}
                    {f.reasoning ? ` · ${f.reasoning}` : ''}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Button size="sm" variant="ghost" className="text-slate-400 hover:text-slate-600"
                    onClick={() => dismissOne(f)} data-testid="finding-dismiss-btn">
                    Not an issue
                  </Button>
                  <Button size="sm" disabled={!!fixing[f.asset_id]}
                    onClick={() => fixOne(f)}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="finding-fix-btn">
                    {fixing[f.asset_id] ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5 mr-1" />}
                    Move to {f.verified_developer}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </>
  );
}

/* ------------------------------------------------------------------ */
/* Recent moves + Undo (shared)                                        */
/* ------------------------------------------------------------------ */
function RecentMovesPanel({ refreshKey }) {
  const [moves, setMoves] = useState([]);
  const [undoing, setUndoing] = useState({});
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/reassign-assets/history?limit=25`, { withCredentials: true });
      setMoves(data.moves || []);
    } catch (e) { /* ignore */ }
  }, []);
  useEffect(() => { load(); const t = setInterval(load, 5000); return () => clearInterval(t); }, [load, refreshKey]);

  const undo = async (m) => {
    setUndoing(prev => ({ ...prev, [m.audit_id]: true }));
    try {
      const { data } = await axios.post(`${API}/admin/reassign-assets/undo`,
        { audit_id: m.audit_id }, { withCredentials: true });
      toast.success(`Undone — ${data.assets_restored} asset(s) put back`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Undo failed'); }
    finally { setUndoing(prev => ({ ...prev, [m.audit_id]: false })); }
  };

  const active = moves.filter(m => !m.undone);
  if (active.length === 0) return null;

  return (
    <Card className="border-slate-200 mb-5 bg-slate-50/60" data-testid="recent-moves-panel">
      <CardContent className="p-3.5">
        <button className="flex items-center gap-2 text-sm font-semibold text-slate-700 w-full text-left"
          onClick={() => setOpen(o => !o)} data-testid="recent-moves-toggle">
          <RotateCcw className="w-4 h-4 text-slate-500" />
          Recent moves ({active.length}) — click to {open ? 'hide' : 'undo any'}
        </button>
        {open && (
          <div className="mt-3 space-y-2">
            {active.map(m => (
              <div key={m.audit_id} className="flex items-center justify-between gap-3 bg-white border border-slate-200 rounded-lg p-2.5">
                <div className="min-w-0 text-xs text-slate-600">
                  Moved <span className="font-semibold">{m.assets_moved}</span> asset(s) → <span className="font-semibold">{m.target_company_name}</span>
                  <span className="text-slate-400"> · {m.created_at ? new Date(m.created_at).toLocaleString() : ''}</span>
                  {m.asset_names?.length ? <div className="text-slate-400 truncate">{m.asset_names.join(', ')}</div> : null}
                </div>
                <Button size="sm" variant="outline" disabled={!!undoing[m.audit_id]}
                  onClick={() => undo(m)} className="border-amber-300 text-amber-800 hover:bg-amber-50 shrink-0"
                  data-testid="undo-move-btn">
                  {undoing[m.audit_id] ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Undo2 className="w-3.5 h-3.5 mr-1" />}
                  Undo
                </Button>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/* ------------------------------------------------------------------ */
export default function MergeRecoveryPage() {
  return (
    <div className="max-w-5xl mx-auto px-6 py-8" data-testid="merge-recovery-page">
      <div className="flex items-start gap-3 mb-5">
        <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center shrink-0">
          <Undo2 className="w-5 h-5 text-amber-700" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Data Recovery</h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Fix companies that got their assets crossed — either by reversing a bad merge or by
            moving mis-assigned assets to the right company.
          </p>
        </div>
      </div>

      <RecentMovesPanel />

      <Tabs defaultValue="scan">
        <TabsList className="mb-5">
          <TabsTrigger value="scan" data-testid="tab-scan">AI Scan (whole database)</TabsTrigger>
          <TabsTrigger value="fix" data-testid="tab-fix">Fix Asset Ownership</TabsTrigger>
          <TabsTrigger value="unmerge" data-testid="tab-unmerge">Un-merge Companies</TabsTrigger>
        </TabsList>
        <TabsContent value="scan"><AiScanTab /></TabsContent>
        <TabsContent value="fix"><FixOwnershipTab /></TabsContent>
        <TabsContent value="unmerge"><UnmergeTab /></TabsContent>
      </Tabs>
    </div>
  );
}
